import React from 'react';
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Alert, AlertDescription } from "../ui/alert";
import { Upload, FileText, Trash2, Info } from "lucide-react";
import { GpxTraceEditor } from "./GpxTraceEditor";
import { TrailCategory } from '../../types/trail';

interface GpxSectionProps {
    category: TrailCategory;
    dogGpxFile: File | null;
    userGpxFile: File | null;
    dogGpxData: any;
    userGpxData: any;
    gpxError: string;
    handleGPXUpload: (type: "dog" | "user") => (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
    handleRemoveGPX: (type: "dog" | "user") => () => void;
    handleUpdateDogPath: (newPath: [number, number][], newTimestamps?: (string | Date)[]) => void;
    handlePreviewDogPath: (previewPath: [number, number][], newTimestamps?: (string | Date)[]) => void;
    handleUpdateUserPath: (newPath: [number, number][], newTimestamps?: (string | Date)[]) => void;
    handlePreviewUserPath: (previewPath: [number, number][], newTimestamps?: (string | Date)[]) => void;
}

export function GpxSection({
    category,
    dogGpxFile,
    userGpxFile,
    dogGpxData,
    userGpxData,
    gpxError,
    handleGPXUpload,
    handleRemoveGPX,
    handleUpdateDogPath,
    handlePreviewDogPath,
    handleUpdateUserPath,
    handlePreviewUserPath,
}: GpxSectionProps) {
    return (
        <>
            {/* GPX Upload */}
            <div className="space-y-4">
              <h3 className="text-lg text-blue-900">Fichiers GPX</h3>
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Les fichiers GPX permettent d'extraire automatiquement la
                  distance, la durée, le dénivelé et la localisation.
                </AlertDescription>
              </Alert>
              
              {/* Primary track (Dog for mantrailing, User for hiking) */}
              <div className="space-y-3">
                <Label
                  className={
                    category === "mantrailing"
                      ? "text-blue-900"
                      : "text-green-900"
                  }
                >
                  {category === "mantrailing"
                    ? "Trace du chien"
                    : "Votre trace"}
                </Label>

                {!(category === "mantrailing"
                  ? dogGpxFile || dogGpxData
                  : userGpxFile || userGpxData) && (
                  <div
                    className={`border-2 border-dashed rounded-lg p-4 text-center hover:border-${
                      category === "mantrailing" ? "blue" : "green"
                    }-400 transition-colors ${
                      category === "mantrailing"
                        ? "border-blue-200"
                        : "border-green-200"
                    }`}
                  >
                    <label
                      htmlFor={`${category}-primary-gpx-upload`}
                      className="cursor-pointer flex flex-col items-center gap-2"
                    >
                      <div
                        className={`p-2 rounded-full ${
                          category === "mantrailing"
                            ? "bg-blue-100"
                            : "bg-green-100"
                        }`}
                      >
                        <Upload
                          className={`h-5 w-5 ${
                            category === "mantrailing"
                              ? "text-blue-600"
                              : "text-green-600"
                          }`}
                        />
                      </div>
                      <div>
                        <p
                          className={`text-sm ${
                            category === "mantrailing"
                              ? "text-blue-900"
                              : "text-green-900"
                          }`}
                        >
                          {category === "mantrailing"
                            ? "Trace du chien"
                            : "Votre trace"}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Fichier .gpx
                        </p>
                      </div>
                    </label>
                    <input
                      id={`${category}-primary-gpx-upload`}
                      type="file"
                      accept=".gpx"
                      onChange={handleGPXUpload(
                        category === "mantrailing" ? "dog" : "user"
                      )}
                      className="hidden"
                    />
                  </div>
                )}

                {((category === "mantrailing" &&
                  (dogGpxFile || dogGpxData)) ||
                  ((category === "hiking" || category === "canicross") &&
                    (userGpxFile || userGpxData))) && (
                  <div
                    className={`bg-gradient-to-r rounded-lg p-3 border-2 ${
                      category === "mantrailing"
                        ? "from-blue-50 to-blue-100 border-blue-200"
                        : "from-green-50 to-green-100 border-green-200"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <FileText
                        className={`h-5 w-5 mt-0.5 ${
                          category === "mantrailing"
                            ? "text-blue-600"
                            : "text-green-600"
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        <p
                          className={`text-sm ${
                            category === "mantrailing"
                              ? "text-blue-900"
                              : "text-green-900"
                          }`}
                        >
                          {category === "mantrailing"
                            ? dogGpxFile?.name || "Trace du chien (existante)"
                            : userGpxFile?.name || "Votre trace (existante)"}
                        </p>
                        {((category === "mantrailing" && dogGpxData) ||
                          ((category === "hiking" || category === "canicross") && userGpxData)) && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {Math.round(
                              (category === "mantrailing"
                                ? dogGpxData.distance
                                : userGpxData.distance) || 0
                            )}
                            m •{" "}
                            {(category === "mantrailing"
                              ? dogGpxData
                              : userGpxData
                            )?.path?.length || 0}{" "}
                            points
                          </p>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={handleRemoveGPX(
                          category === "mantrailing" ? "dog" : "user"
                        )}
                        className={`h-8 w-8 ${
                          category === "mantrailing"
                            ? "text-blue-700 hover:text-blue-900 hover:bg-blue-200"
                            : "text-green-700 hover:text-green-900 hover:bg-green-200"
                        }`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Secondary track (Runner for mantrailing, Dog for hiking) */}
              {category !== "canicross" && <div className="space-y-3">
                <Label className="text-orange-900">
                  {category === "mantrailing"
                    ? "Trace de la victime / coureur"
                    : "Trace du chien (optionnelle)"}
                </Label>

                {!(category === "mantrailing"
                  ? userGpxFile || userGpxData
                  : dogGpxFile || dogGpxData) && (
                  <div className="border-2 border-dashed border-orange-200 rounded-lg p-4 text-center hover:border-orange-400 transition-colors">
                    <label
                      htmlFor={`${category}-secondary-gpx-upload`}
                      className="cursor-pointer flex flex-col items-center gap-2"
                    >
                      <div className="bg-orange-100 p-2 rounded-full">
                        <Upload className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-sm text-orange-900">
                          {category === "mantrailing"
                            ? "Trace de la victime / coureur"
                            : "Trace du chien"}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Fichier .gpx (optionnel)
                        </p>
                      </div>
                    </label>
                    <input
                      id={`${category}-secondary-gpx-upload`}
                      type="file"
                      accept=".gpx"
                      onChange={handleGPXUpload(
                        category === "mantrailing" ? "user" : "dog"
                      )}
                      className="hidden"
                    />
                  </div>
                )}

                {((category === "mantrailing" &&
                  (userGpxFile || userGpxData)) ||
                  (category === "hiking" && (dogGpxFile || dogGpxData))) && (
                  <div className="bg-gradient-to-r from-orange-50 to-orange-100 border-2 border-orange-200 rounded-lg p-3">
                    <div className="flex items-start gap-3">
                      <FileText className="h-5 w-5 text-orange-600 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-orange-900">
                          {category === "mantrailing"
                            ? userGpxFile?.name ||
                              "Trace de la victime (existante)"
                            : dogGpxFile?.name ||
                              "Trace du chien (existante)"}
                        </p>
                        {((category === "mantrailing" && userGpxData) ||
                          (category === "hiking" && dogGpxData)) && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {Math.round(
                              (category === "mantrailing"
                                ? userGpxData.distance
                                : dogGpxData.distance) || 0
                            )}
                            m •{" "}
                            {(category === "mantrailing"
                              ? userGpxData
                              : dogGpxData
                            )?.path?.length || 0}{" "}
                            points
                          </p>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={handleRemoveGPX(
                          category === "mantrailing" ? "user" : "dog"
                        )}
                        className="text-orange-700 hover:text-orange-900 hover:bg-orange-200 h-8 w-8"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>}

              {gpxError && (
                <Alert variant="destructive">
                  <AlertDescription>{gpxError}</AlertDescription>
                </Alert>
              )}
            </div>

            {/* Trace Editors */}
            <div className="space-y-4">
              {((category === "mantrailing" && dogGpxData) ||
                ((category === "hiking" || category === "canicross") && userGpxData)) && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-900">
                    💡 <strong>Ajustez vos traces :</strong> Déplacez les curseurs pour sélectionner la portion de trace à conserver.
                  </p>
                </div>
              )}

              {category === "mantrailing" && dogGpxData && (
                <div className="mt-4">
                  <GpxTraceEditor
                    path={dogGpxData.path}
                    timestamps={dogGpxData.timestamps}
                    label="Trace du chien"
                    color="#3b82f6"
                    onUpdate={handleUpdateDogPath}
                    onPreview={handlePreviewDogPath}
                  />
                </div>
              )}

              {(category === "hiking" || category === "canicross") && userGpxData && (
                <div className="mt-4">
                  <GpxTraceEditor
                    path={userGpxData.path}
                    timestamps={userGpxData.timestamps}
                    label="Votre trace"
                    color={category === "canicross" ? "#f97316" : "#22c55e"}
                    onUpdate={handleUpdateUserPath}
                    onPreview={handlePreviewUserPath}
                  />
                </div>
              )}

              {category === "mantrailing" && userGpxData && (
                <div className="mt-4">
                  <GpxTraceEditor
                    path={userGpxData.path}
                    timestamps={userGpxData.timestamps}
                    label="Trace de la victime"
                    color="#f97316"
                    onUpdate={handleUpdateUserPath}
                    onPreview={handlePreviewUserPath}
                  />
                </div>
              )}

              {category === "hiking" && dogGpxData && (
                <div className="mt-4">
                  <GpxTraceEditor
                    path={dogGpxData.path}
                    timestamps={dogGpxData.timestamps}
                    label="Trace du chien"
                    color="#3b82f6"
                    onUpdate={handleUpdateDogPath}
                    onPreview={handlePreviewDogPath}
                  />
                </div>
              )}
            </div>
        </>
    );
}
